import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store";
import OrganisationLayout from "../../components/organisation/layout/OrganisationLayout";
import { FilterMatchMode } from "primereact/api";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import GreendotDot from "../../assets/icons/GreenDotIcon.svg";
import { getFromSessionStorage } from "../../utils/browserStorage";
import { fetchAllUsers } from "../../store/slices/users/action";
import { toast } from "react-toastify";
import InviteUser from "../../components/modalOperations/InviteUser";
import { BaseButton } from "../../components/base";
import { BiSearchAlt } from "react-icons/bi";
import { roleTypes } from "../../data/utils";

const Users = () => {
  const [open, setInviteModalOpen] = useState(false);
  const { usersData } = useAppSelector((state) => state.userReducer);
  const { currentOrganisationAccount } = useAppSelector(
    (state) => state.authReducer
  );

  const headerStyle = {
    color: "#0C4654",
    fontSize: "0.875rem",
    fontWeight: "500",
    backgroundColor: "transparent",
  };
  const bodyStyle = {
    color: "#0C4654",
    fontSize: "1rem",
    fontWeight: "500",
    backgroundColor: "transparent",
  };

  const openModal = () => {
    setInviteModalOpen(true);
  };

  const dispatch = useAppDispatch();

  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState(null);

  const [lazyParams, setLazyParams] = useState({
    organisationId: currentOrganisationAccount?.organisationId || "",
    first: 0,
    rows: 10,
    page: 0,
    filters: {
      global: { value: "", matchMode: FilterMatchMode.CONTAINS },
    },
  });

  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLazyData(lazyParams);
  }, []);

  const loadLazyData = async (lazyParams: any) => {
    setLoading(true);
    const res = await dispatch(fetchAllUsers(lazyParams));

    if (res?.payload?.status == 200) {
      setLoading(false);
    } else {
      toast.error("Error fetching users. Pls try again");
      setLoading(false);
    }
  };

  const onPage = (event: any) => {
    setLazyParams({
      filters: lazyParams.filters,
      organisationId: lazyParams.organisationId,
      first: event.first,
      rows: event.rows,
      page: event.page,
    });

    loadLazyData({
      filters: lazyParams.filters,
      organisationId: lazyParams.organisationId,
      first: event.first,
      rows: event.rows,
      page: event.page,
    });
  };

  const onGlobalFilterChange = (e: any) => {
    const value = e.target.value;
    if (e.target.value.length >= 3) {
      let _filters = { ...lazyParams.filters };
      _filters["global"].value = value;
      setGlobalFilterValue(value);
      setLazyParams({ ...lazyParams, filters: _filters });
      loadLazyData({
        ...lazyParams,
        filters: _filters,
      });
    }
    if (e.target.value === "") {
      loadLazyData({
        ...lazyParams,
        filters: {
          global: { value: "", matchMode: FilterMatchMode.CONTAINS },
        },
      });
    }
  };

  const onSort = (event: any) => {
    setSortOrder(event.sortOrder);
    setSortField(event.sortField);
    const sortField = event.sortField.split(".")[1] ?? event.sortField;
    loadLazyData({
      ...lazyParams,
      sortField,
      sortOrder: event.sortOrder === -1 ? "asc" : "desc",
    });
  };

  const fullNameBodyTemplate = (rowData: any) => {
    return (
      <div className="flex items-center">
        <img
          src={GreendotDot}
          alt="green dot icon"
          className="w-[6px] xl:w-[10px] mx-2 flex my-auto"
        />
        <p>
          {rowData?.firstName} {rowData?.lastName}{" "}
        </p>
      </div>
    );
  };

  const roleBodyTemplate = (rowData: any) => {
    const role = roleTypes.find((role) => role?.id === rowData?.roles[0]);
    return (
      <div className="flex items-center">
        <p>{role?.name || "--------"}</p>
      </div>
    );
  };

  const createdAtBodyTemplate = (rowData: any) =>
    rowData?.createdAt ? (
      <div>
        <span>
          {
            new Date(rowData?.createdAt)
              .toLocaleString("en-GB", { timeZone: "UTC" })
              .split(",")[0]
          }
        </span>
      </div>
    ) : (
      "----------"
    );

  const renderHeader = () => {
    return (
      <div className="  w-full ">
        <span className="p-input-icon-left bg-white py-2 w-full">
          <i className="pi pi-search " />
          <InputText
            style={{
              paddingLeft: "2rem",
              width: "100%",
              outline: "none",
            }}
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Search"
          />
        </span>
      </div>
    );
  };
  const header = renderHeader();

  return (
    <>
      <OrganisationLayout>
        <InviteUser isOpen={open} setOpen={setInviteModalOpen} />
        <div className="px-3 lg:px-12 mt-5">
          <div className="mb-6">
            <h1 className="text-[#0C4654] font-[500] text-[1.2rem]">Users</h1>
          </div>
          <div className="bg-[#F2F6FA] rounded px-3 py-6 text-[#0C4654]">
            <div className="w-full flex justify-end mb-4">
              <BaseButton
                text="Invite a User"
                variant="primary"
                styles="!px-5 !h-10 my-1 !text-[0.875rem] "
                click={openModal}
              />
            </div>
            <div className="mb-4 px-3">
              <label className="relative">
                <BiSearchAlt
                  className="textbrandColor absolute left-3 top-1"
                  size="1rem"
                />
                <input
                  type="text"
                  className="border p-4 pl-8 rounded-[0.5rem] w-full text-[0.875rem] font-[500] placeholder-[#767B86] focus:outline-none"
                  placeholder="Search"
                  onKeyUp={onGlobalFilterChange}
                />
              </label>
            </div>
            <div className="p-3 mt-2 rounded-[8px]">
              <DataTable
                value={usersData?.data}
                lazy
                paginator
                className="h-full"
                // header={header}
                first={lazyParams?.first}
                rows={lazyParams?.rows}
                totalRecords={usersData?.total}
                onPage={onPage}
                // onSort={onSort}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                rowsPerPageOptions={[5, 10, 25, 50]}
                dataKey="id"
                rowHover
                sortField={sortField}
                sortOrder={sortOrder}
                scrollable
                loading={loading}
                selectionMode="single"
                responsiveLayout="scroll"
                globalFilterFields={["name", "unit"]}
                emptyMessage="No users found."
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} users"
              >
                <Column
                  header="Name"
                  body={fullNameBodyTemplate}
                  headerStyle={headerStyle}
                  style={{
                    fontWeight: "700",
                    borderBottom: "5px solid #F9FAFB",
                  }}
                  bodyStyle={bodyStyle}
                />
                <Column
                  field="email"
                  header="Email Address"
                  headerStyle={headerStyle}
                  style={{
                    fontWeight: "700",
                    borderBottom: "5px solid #F9FAFB",
                  }}
                  bodyStyle={bodyStyle}
                />
                <Column
                  field="role"
                  header="Role"
                  headerStyle={headerStyle}
                  style={{
                    fontWeight: "700",
                    borderBottom: "5px solid #F9FAFB",
                  }}
                  body={roleBodyTemplate}
                  bodyStyle={bodyStyle}
                />
                <Column
                  field="uniqueId"
                  header="Unique Id"
                  headerStyle={headerStyle}
                  style={{
                    fontWeight: "700",
                    borderBottom: "5px solid #F9FAFB",
                  }}
                  bodyStyle={bodyStyle}
                />
                <Column
                  field="createdDate"
                  header="Created Date"
                  body={createdAtBodyTemplate}
                  headerStyle={headerStyle}
                  style={{
                    fontWeight: "700",
                    borderBottom: "5px solid #F9FAFB",
                  }}
                  bodyStyle={bodyStyle}
                />
              </DataTable>
            </div>
          </div>
        </div>
      </OrganisationLayout>
    </>
  );
};

export default Users;
